import { supabase, SupabaseNotConfiguredError } from "../supabase";
import type { ConceptForm } from "../db";
import type { Database } from "../database.types";

type FormDbRow = Database["public"]["Tables"]["concept_forms"]["Row"];
type FormInsert = Database["public"]["Tables"]["concept_forms"]["Insert"];

function rowToConceptForm(row: FormDbRow & { sync_version?: number }): ConceptForm {
  return {
    syncVersion: row.sync_version,
    key: row.key,
    conceptId: row.concept_id ?? "",
    lang: (row.lang as ConceptForm["lang"]) ?? ("en" as ConceptForm["lang"]),
    texts: row.texts ?? [],
  };
}

function conceptFormToInsert(userId: string, f: ConceptForm): FormInsert {
  return {
    key: f.key,
    owner_id: userId,
    concept_id: f.conceptId,
    lang: f.lang,
    texts: f.texts,
  };
}

const CHUNK_SIZE = 500;

export async function pushConceptForms(userId: string, forms: ConceptForm[]): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  for (let i = 0; i < forms.length; i += CHUNK_SIZE) {
    const chunk = forms.slice(i, i + CHUNK_SIZE).map((f) => conceptFormToInsert(userId, f));
    const { error } = await supabase.from("concept_forms").upsert(chunk, { onConflict: "owner_id,key" });
    if (error) throw error;
  }
}

export async function pushConceptForm(userId: string, form: ConceptForm): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { error } = await supabase
    .from("concept_forms")
    .upsert(conceptFormToInsert(userId, form), { onConflict: "owner_id,key" });
  if (error) throw error;
}

export async function pullConceptForms(userId: string): Promise<ConceptForm[]> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const rows: FormDbRow[] = [];
  let after: string | undefined;
  for (;;) {
    let query = supabase.from("concept_forms").select("*")
      .eq("owner_id", userId).order("key").limit(500);
    if (after !== undefined) query = query.gt("key", after);
    const { data, error } = await query;
    if (error) throw error;
    rows.push(...(data ?? []));
    if ((data ?? []).length < 500) break;
    after = data![data!.length - 1].key;
  }
  return rows.map(rowToConceptForm);
}

export async function deleteConceptFormRemote(userId: string, key: string): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { error } = await supabase.from("concept_forms").delete().eq("key", key).eq("owner_id", userId);
  if (error) throw error;
}
